import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';

interface CookiePreferences {
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
}

interface CookieBannerProps {
    onConsentChange?: (preferences: CookiePreferences) => void;
}

const CookieBanner: React.FC<CookieBannerProps> = ({ onConsentChange }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [preferences, setPreferences] = useState<CookiePreferences>({
        necessary: true, // Always true, can't be disabled
        analytics: false,
        marketing: false
    });

    useEffect(() => {
        // Check if user has already made a choice
        const consent = localStorage.getItem('cookie-consent');
        if (!consent) {
            setIsVisible(true);
        } else {
            // Load saved preferences
            try {
                const savedPreferences = JSON.parse(consent);
                setPreferences(savedPreferences);
                onConsentChange?.(savedPreferences);
            } catch {
                // If parsing fails, show banner again
                setIsVisible(true);
            }
        }
    }, [onConsentChange]);

    const handleAcceptAll = () => {
        const newPreferences = {
            necessary: true,
            analytics: true,
            marketing: true
        };
        savePreferences(newPreferences);
    };

    const handleAcceptNecessary = () => {
        const newPreferences = {
            necessary: true,
            analytics: false,
            marketing: false
        };
        savePreferences(newPreferences);
    };

    const handleSaveSettings = () => {
        savePreferences(preferences);
    };

    const savePreferences = (prefs: CookiePreferences) => {
        localStorage.setItem('cookie-consent', JSON.stringify(prefs));
        localStorage.setItem('cookie-consent-date', new Date().toISOString());
        setPreferences(prefs);
        onConsentChange?.(prefs);
        setIsVisible(false);
        setShowSettings(false);
    };

    const handlePreferenceChange = (type: keyof CookiePreferences, value: boolean) => {
        if (type === 'necessary') return; // Can't disable necessary cookies
        setPreferences((prev) => ({
            ...prev,
            [type]: value
        }));
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-4 left-4 right-4 md:left-8 md:right-8 lg:left-auto lg:right-8 lg:max-w-lg bg-white rounded-lg shadow-2xl border z-50">
            {!showSettings ? (
                // Main banner
                <div className="p-6">
                    <div className="flex items-start gap-3 mb-4">
                        <Cookie className="h-6 w-6 text-primary-600 mt-1 shrink-0" />
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                We value your privacy
                            </h3>
                            <p className="text-sm text-gray-600 leading-relaxed">
                                We use cookies to enhance your experience, analyze site traffic, and
                                personalize content. You can choose which types of cookies you want
                                to allow.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={handleAcceptAll}
                            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                        >
                            Accept All
                        </button>
                        <button
                            onClick={handleAcceptNecessary}
                            className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                        >
                            Necessary Only
                        </button>
                        <button
                            onClick={() => setShowSettings(true)}
                            className="bg-white text-primary-600 border border-primary-600 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors font-medium flex items-center gap-2"
                        >
                            <Settings className="h-4 w-4" />
                            Settings
                        </button>
                    </div>
                </div>
            ) : (
                // Settings panel
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Cookie Settings</h3>
                        <button
                            onClick={() => setShowSettings(false)}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    <div className="space-y-4 mb-6">
                        {/* Necessary Cookies */}
                        <div className="flex items-start justify-between">
                            <div className="flex-1 mr-4">
                                <h4 className="font-medium text-gray-900">Necessary Cookies</h4>
                                <p className="text-sm text-gray-600">
                                    Required for the website to function properly. Cannot be
                                    disabled.
                                </p>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={true}
                                    disabled
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 opacity-50"
                                />
                            </div>
                        </div>

                        {/* Analytics Cookies */}
                        <div className="flex items-start justify-between">
                            <div className="flex-1 mr-4">
                                <h4 className="font-medium text-gray-900">Analytics Cookies</h4>
                                <p className="text-sm text-gray-600">
                                    Help us understand how visitors interact with our website.
                                </p>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={preferences.analytics}
                                    onChange={(e) =>
                                        handlePreferenceChange('analytics', e.target.checked)
                                    }
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                            </div>
                        </div>

                        {/* Marketing Cookies */}
                        <div className="flex items-start justify-between">
                            <div className="flex-1 mr-4">
                                <h4 className="font-medium text-gray-900">Marketing Cookies</h4>
                                <p className="text-sm text-gray-600">
                                    Used to deliver personalized advertisements and track their
                                    effectiveness.
                                </p>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={preferences.marketing}
                                    onChange={(e) =>
                                        handlePreferenceChange('marketing', e.target.checked)
                                    }
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={handleSaveSettings}
                            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                        >
                            Save Preferences
                        </button>
                        <button
                            onClick={handleAcceptAll}
                            className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                        >
                            Accept All
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CookieBanner;
